import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Divider,
} from '@heroui/react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useSegment from '@/hooks/useSegment';
import { EduModal } from '@/components/education/EduModal';
import { useReadEventMutation } from '@/patient-app-common/api/patientApi';
import { AppEvent } from '@/patient-app-common/types/ApplicationEvent';

interface EduEventProps {
  avatar: string | undefined;
  disableRead?: boolean;
  event: AppEvent;
}

const EVENT = 'Education Event Viewed';

export function EduEvent({
  avatar,
  disableRead = false,
  event,
}: EduEventProps) {
  const { t } = useTranslation('home');
  const [eduModal, toggleEduModal] = useState(false);
  const { sendTrackEvent } = useSegment();
  const [readEvent] = useReadEventMutation();

  const handleReadClick = async (event: AppEvent) => {
    try {
      await readEvent({ eventId: event.id }).unwrap();
      sendTrackEvent(EVENT, { id: [event.id], title: [event.title] });
    } catch (e) {
      console.log(e);
    }
  };

  const isSticky = disableRead || event.shouldPersistAfterReading;

  return (
    <>
      <EduModal
        event={event}
        source="EduEvent"
        onClose={() => toggleEduModal(false)}
        isOpen={eduModal}
      />
      <article>
        <Card radius="sm" shadow="sm">
          <p className="font-light p-2 text-sm">
            {dayjs(event.startDateTime).format('LL')}
          </p>
          <CardHeader>
            <Avatar
              radius="full"
              size="md"
              src={avatar}
              classNames={{ base: 'bg-avatarBg' }}
            />
            <h2 className="font-semibold px-4">{event.title}</h2>
          </CardHeader>
          <Divider />
          <CardBody>
            <p className="px-8 whitespace-pre-line">{event.body}</p>
          </CardBody>
          <CardFooter className="justify-end gap-2">
            {!_.isEmpty(event.edIds) && !_.isEmpty(event.options.linkText) && (
              <Button
                color="primary"
                onPress={() => toggleEduModal(true)}
                variant="bordered"
              >
                {event.options.linkText}
              </Button>
            )}
            {!isSticky && (
              <Button color="primary" onPress={() => handleReadClick(event)}>
                {t('eduevent.button.read')}
              </Button>
            )}
          </CardFooter>
        </Card>
      </article>
    </>
  );
}
